/**
 * Contextual FoxChat trigger evaluator.
 *
 * Each trigger is a (page_url_pattern, condition, action) tuple in Postgres.
 * We load the active rules once per session, then re-evaluate them every 10 s
 * while the user sits on a dashboard page. When one fires, we run its action
 * (bubble / highlight / open chat / open resource center) and mark the trigger
 * as fired for this page+session so it can't fire twice.
 *
 * Condition syntax is deliberately small — flat AND or flat OR of
 * `<identifier> <op> <value>` clauses — so we can parse it without resorting
 * to `eval`. Supported ops: >, <, >=, <=, =, ==, !=, contains. Identifiers:
 *   time_on_page              — seconds since the current page loaded
 *   clicks_in_page            — user-generated clicks this page visit
 *   seconds_since_last_action — idle time
 *   page_url                  — current pathname, string compare
 *   <anything else>           — looked up in window.__foxchatSignals (pages push
 *                               custom page-scoped signals like pending_rows here)
 */
import { supabase } from "@/integrations/supabase/client";

export interface FoxchatSignals {
  // Pages push whatever they want here — triggers can reference any key.
  [key: string]: string | number | boolean;
}

declare global {
  interface Window {
    __foxchatSignals?: FoxchatSignals;
  }
}

export interface TriggerRow {
  id: string;
  name: string;
  page_url_pattern: string;
  condition: string;
  action: string; // JSON string
  priority: number;
}

export interface TriggerAction {
  type: "bubble" | "highlight" | "open_chat" | "open_resource_center";
  message?: string;
  highlight_id?: string | null;
  dismissible?: boolean;
  open_resource_center_tab?: string;
}

// ── Pattern matching ────────────────────────────────────────────────────────
export function matchesPattern(pathname: string, pattern: string): boolean {
  // Trivial glob: trailing `*` matches anything.
  if (!pattern) return false;
  if (pattern === pathname) return true;
  if (pattern.endsWith("*")) {
    const prefix = pattern.slice(0, -1);
    return pathname.startsWith(prefix);
  }
  return pathname === pattern;
}

// ── Expression parser ──────────────────────────────────────────────────────
// Tokenises on AND / OR, then on each clause splits identifier, op, value.
// Returns a function (context) -> boolean, or a no-op that returns false on
// parse errors so bad rules never crash the UI.
const CLAUSE_RE = /^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(>=|<=|!=|==|=|>|<|contains)\s*(.+?)\s*$/;
const COMBINATOR_RE = /\b(AND|OR)\b/i;

type Ctx = Record<string, string | number | boolean>;

function parseValue(raw: string): string | number | boolean {
  const t = raw.trim().replace(/^["']|["']$/g, "");
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  if (t.toLowerCase() === "true") return true;
  if (t.toLowerCase() === "false") return false;
  return t;
}

function evalClause(ident: string, op: string, value: any, ctx: Ctx): boolean {
  const a = ctx[ident];
  if (op === "contains") {
    return String(a ?? "").toLowerCase().includes(String(value).toLowerCase());
  }
  const av = typeof a === "number" ? a : Number.isFinite(Number(a)) ? Number(a) : a;
  const bv = typeof value === "number" ? value : Number.isFinite(Number(value)) ? Number(value) : value;
  switch (op) {
    case ">":  return Number(av) >   Number(bv);
    case "<":  return Number(av) <   Number(bv);
    case ">=": return Number(av) >=  Number(bv);
    case "<=": return Number(av) <=  Number(bv);
    case "=": case "==": return av == bv; // eslint-disable-line eqeqeq
    case "!=": return av != bv;          // eslint-disable-line eqeqeq
    default: return false;
  }
}

export function compileCondition(expr: string): (ctx: Ctx) => boolean {
  try {
    if (!expr || !expr.trim()) return () => false;
    // Detect the combinator actually used (assume consistent per rule — the seeds are).
    const combinators: string[] = expr.match(new RegExp(COMBINATOR_RE, "gi")) || [];
    const hasAnd = combinators.some((c: string) => c.toUpperCase() === "AND");
    const hasOr = combinators.some((c: string) => c.toUpperCase() === "OR");
    const reduce = hasOr && !hasAnd ? "OR" : "AND";
    const clauses = expr.split(COMBINATOR_RE).filter((s) => s && !/^(AND|OR)$/i.test(s));
    const compiled = clauses.map((c) => {
      const m = c.match(CLAUSE_RE);
      if (!m) return () => false;
      const ident = m[1];
      const op = m[2];
      const value = parseValue(m[3]);
      return (ctx: Ctx) => evalClause(ident, op, value, ctx);
    });
    if (compiled.length === 0) return () => false;
    return (ctx: Ctx) => reduce === "OR" ? compiled.some((fn) => fn(ctx)) : compiled.every((fn) => fn(ctx));
  } catch {
    return () => false;
  }
}

// ── DB loader ───────────────────────────────────────────────────────────────
export async function loadActiveTriggers(): Promise<TriggerRow[]> {
  const { data } = await supabase
    .from("foxchat_triggers" as any)
    .select("id, name, page_url_pattern, condition, action, priority")
    .eq("enabled", true)
    .order("priority", { ascending: false });
  return ((data as any[]) || []) as TriggerRow[];
}

export function parseAction(raw: string): TriggerAction | null {
  try {
    const a = JSON.parse(raw);
    if (!a || typeof a !== "object" || !a.type) return null;
    return a as TriggerAction;
  } catch { return null; }
}

// ── Session-level firing guard ─────────────────────────────────────────────
const FIRED_KEY = "foxchat_triggers_fired_v1";
function firedRecord(): Record<string, true> {
  try { return JSON.parse(sessionStorage.getItem(FIRED_KEY) || "{}"); } catch { return {}; }
}
export function hasFired(triggerId: string, pathname: string): boolean {
  return !!firedRecord()[triggerId + ":" + pathname];
}
export function markFired(triggerId: string, pathname: string) {
  try {
    const r = firedRecord();
    r[triggerId + ":" + pathname] = true;
    sessionStorage.setItem(FIRED_KEY, JSON.stringify(r));
  } catch { /* ignore */ }
}

// Whether ANY trigger has fired on the current page+session. Enforces the
// "Max 1 trigger per page per session" rule from the spec.
export function anyFiredOnPage(pathname: string): boolean {
  const r = firedRecord();
  const suffix = ":" + pathname;
  return Object.keys(r).some((k) => k.endsWith(suffix));
}
